import { MutableRefObject, useRef } from 'react';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { useReceivableId, useReceivableRegistry } from './ReceivableRegistry';
import {
  ReceivableChanges,
  useReceivablePersistence,
} from './useReceivablePersistence';

/**
 * Shared optimistic-write core for the editable receivable cells
 * (SupplyPro / Billed / Invoice).
 *
 * Every cell writes through one idempotent PUT keyed by the phase, so a row
 * without a receivable yet needs no create-or-update decision and two cells
 * saved together no longer race on the create (the API serializes them).
 *
 * - `receivableId` is the effective id (the record's, or the one the API
 *   returned this session and the registry remembered). Pass it to the
 *   history modal so a sibling sees the new id without a refetch.
 * - `pendingRef` is true for the duration of THIS cell's own save. The cell
 *   guards its own "resync display state from `record`" effect with it, so an
 *   incoming `record` re-render can't clobber this cell's in-flight edit.
 * - `commit` sends the changed keys only and rethrows on failure so the caller
 *   can revert + notify.
 */
export const useOptimisticReceivable = (
  record: JobPhaseReceivableSheetInterface | undefined,
): {
  receivableId: string | null;
  pendingRef: MutableRefObject<boolean>;
  commit: (changes: ReceivableChanges) => Promise<void>;
} => {
  const { save } = useReceivablePersistence();
  const registry = useReceivableRegistry();
  const pendingRef = useRef(false);

  const receivableId = useReceivableId(record);

  const commit = async (changes: ReceivableChanges): Promise<void> => {
    if (!record) return;

    pendingRef.current = true;
    try {
      const saved = await save({ jobPhaseId: record.jobPhaseId, changes });
      registry.setId(record.jobPhaseId, saved.id);
    } finally {
      pendingRef.current = false;
    }
  };

  return { receivableId, pendingRef, commit };
};
